import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

interface Props {
  open: boolean;
  /** The element the menu is anchored to (chip / pill). */
  triggerRef: React.RefObject<HTMLElement>;
  /** Fires when the user clicks outside the trigger + menu. */
  onDismiss: () => void;
  /** Class applied to the rendered menu shell (e.g. `pm-status-menu`). */
  className?: string;
  /** Approximate menu height in px — used only for the first paint before
   *  we can measure the real menu node. */
  estimatedHeight?: number;
  children: React.ReactNode;
}

// Renders a dropdown menu via React portal at document.body, with
// `position: fixed` coordinates computed from the trigger's bounding rect.
// The portal escapes any ancestor `overflow: hidden` (workstream cards clip
// their corners that way) so the menu can extend past the parent's bounds.
// Placement prefers below; flips above when there isn't room below and
// there's more space above.
const PortalMenu: React.FC<Props> = ({
  open,
  triggerRef,
  onDismiss,
  className = '',
  estimatedHeight = 200,
  children,
}) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  // Click-outside dismiss.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      onDismiss();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, onDismiss, triggerRef]);

  // Position the menu relative to the trigger. We measure on open and on
  // scroll/resize so the menu stays glued to the trigger as the page moves.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const trig = triggerRef.current;
      if (!trig) return;
      const r = trig.getBoundingClientRect();
      const h = menuRef.current?.offsetHeight || estimatedHeight;
      const w = menuRef.current?.offsetWidth || 200;
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      const spaceBelow = vh - r.bottom;
      const spaceAbove = r.top;
      const openUp = spaceBelow < h + 8 && spaceAbove > spaceBelow;
      const top = openUp
        ? Math.max(8, r.top - h - 6)
        : Math.min(vh - h - 8, r.bottom + 6);
      const left = Math.max(8, Math.min(r.left, vw - w - 8));
      setCoords({ top, left });
    };
    place();
    // Re-measure after the first paint so we use the menu's real height
    // (estimatedHeight is only a placeholder).
    const raf = window.requestAnimationFrame(place);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, triggerRef, estimatedHeight]);

  if (!open || !coords) return null;
  return ReactDOM.createPortal(
    <div
      ref={menuRef}
      className={`${className} pm-app pm-portal-menu`}
      style={{ position: 'fixed', top: coords.top, left: coords.left }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  );
};

export default PortalMenu;
